// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Form component for creating initial true-up.
 *
 * This form allows users to establish Hardfin as an authoritative subledger
 * by creating a one-time true-up journal entry (JE0) to align their GL with
 * Hardfin's computed balances.
 *
 * Prerequisites:
 * - 3 core ledger accounts must be configured: Inventory, Fixed Assets, and
 *   Accumulated Depreciation
 *
 * Form fields:
 * - Year/month selector (defaults to prior month)
 * - Inventory balance (currency input)
 * - Fixed asset cost (currency input)
 * - Accumulated depreciation (currency input)
 * - Onboarding adjustment account name (text input)
 */
import { LedgerAccountType } from '@/account/api/types';
import { useOrganizationLedgerAccounts } from '@/account/hooks/use-organization-ledger-accounts';
import {
  InitialTrueUpCloseRunResponse,
  SavedAccountingPeriod,
} from '@/accounting/api/types';
import { useCreateInitialTrueUp } from '@/accounting/hooks/use-create-initial-true-up';
import { useInitialTrueUps } from '@/accounting/hooks/use-initial-true-ups';
import {
  formatPeriod,
  generateInitialTrueUpPeriodList,
} from '@/accounting/utilities/generate-period-list';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  Autorenew,
  CheckCircleOutlineRounded,
  ErrorOutlineRounded,
  InfoOutlined,
  OpenInNewRounded,
  WarningAmberOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  SerializableCurrency,
  currencyInputSchema,
} from '@/currency/api/types';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { ExceptionSummaryCard } from '@/report/components/exception-summary-card';
import { useDateInconsistencies } from '@/report/hooks/use-date-inconsistencies';
import { useDetailInconsistencies } from '@/report/hooks/use-detail-inconsistencies';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import axios from 'axios';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Ledger account types required for initial true-up.
 * Only these 3 accounts are needed - the onboarding adjustment account
 * is provided as a text input in the form.
 */
const REQUIRED_LEDGER_ACCOUNT_TYPES: LedgerAccountType[] = [
  'INV_INVENTORY',
  'FA_FIXED_ASSETS',
  'FA_ACCUMULATED_DEPRECIATION',
];
const REQUIRED_ACCOUNT_COUNT = REQUIRED_LEDGER_ACCOUNT_TYPES.length;

/**
 * Accumulated depreciation must not exceed fixed asset cost.
 */
const currencyBalancesSchema = z
  .object({
    currency: isoCurrencyCodeSchema,
    glInventory: currencyInputSchema,
    glFixedAssets: currencyInputSchema,
    glAccumulatedDepr: currencyInputSchema,
  })
  .refine(
    (data) => {
      // A failed amount transform leaves a non-Decimal on .amount; the field already carries its
      // own issue, so skip the cross-field comparison rather than call a method that does not exist.
      if (
        !Decimal.isDecimal(data.glAccumulatedDepr.amount) ||
        !Decimal.isDecimal(data.glFixedAssets.amount)
      ) {
        return true;
      }
      return data.glAccumulatedDepr.amount.lessThanOrEqualTo(
        data.glFixedAssets.amount
      );
    },
    {
      message: 'Accumulated depreciation cannot exceed fixed asset cost',
      // FormCurrencyTextField shows errors from .amount, not the object.
      path: ['glAccumulatedDepr', 'amount'],
    }
  );

/**
 * Form validation schema.
 */
const formSchema = z.object({
  year: z.number().min(1970).max(2099),
  month: z.number().min(1).max(12),
  // A single entry for the route's currency.
  balances: z.array(currencyBalancesSchema).min(1),
  onboardingAdjustmentAccount: z
    .string()
    .min(1, 'Onboarding adjustment account name is required'),
});

// The currency fields transform (string amount -> Decimal), so the input shape (bound by the
// form controls) and output shape (submitted) differ.
interface CurrencyBalancesInput {
  currency: IsoCurrencyCode;
  glInventory: SerializableCurrency;
  glFixedAssets: SerializableCurrency;
  glAccumulatedDepr: SerializableCurrency;
}

interface FormData {
  year: number;
  month: number;
  balances: CurrencyBalancesInput[];
  onboardingAdjustmentAccount: string;
}

type FormOutput = z.output<typeof formSchema>;

/**
 * Gets the period key for use as select value.
 */
function getPeriodKey(period: SavedAccountingPeriod): string {
  return `${period.year}-${period.month}`;
}

interface Props {
  // The asset currency this initial true-up onboards, from the route.
  currencyCode: IsoCurrencyCode;
  /**
   * Callback invoked when the initial true-up is created successfully.
   * Passes the full close run response object.
   */
  onSuccess?: (closeRun: InitialTrueUpCloseRunResponse) => void;
}

export function InitialTrueUpForm(props: Props) {
  const { user } = useCurrentUser();
  const orgKey = user.organization.key;
  const organizationTimezone = user.organization.timezone;

  // The latest locked period comes from the org's initial true-ups.
  const { initialTrueUps, isLoading: isLoadingInitialTrueUps } =
    useInitialTrueUps();

  // Fetch ledger accounts to check configuration
  const { data: ledgerAccountsData } = useOrganizationLedgerAccounts(orgKey);

  // Calculate ledger account configuration status (only checking required accounts)
  const configuredCount = REQUIRED_LEDGER_ACCOUNT_TYPES.filter(
    (accountType) => {
      const value = ledgerAccountsData.ledgerAccounts[accountType];
      return value !== null && value !== '' && value !== undefined;
    }
  ).length;

  const ledgerAccountsComplete = configuredCount === REQUIRED_ACCOUNT_COUNT;
  const configurationProgress =
    (configuredCount / REQUIRED_ACCOUNT_COUNT) * 100;

  // Track animation completion state for each exception summary card
  const [isDateAnimationComplete, setIsDateAnimationComplete] = useState(false);
  const [isDetailAnimationComplete, setIsDetailAnimationComplete] =
    useState(false);

  // Fetch exception data to validate before allowing form submission
  const { data: dateInconsistencies, isLoading: isLoadingDate } =
    useDateInconsistencies();
  const { data: detailInconsistencies, isLoading: isLoadingDetail } =
    useDetailInconsistencies();

  // Reset animation completion state when data starts loading
  useEffect(() => {
    if (isLoadingDate) {
      setIsDateAnimationComplete(false);
    }
  }, [isLoadingDate]);

  useEffect(() => {
    if (isLoadingDetail) {
      setIsDetailAnimationComplete(false);
    }
  }, [isLoadingDetail]);

  // Determine if exceptions data is still loading (includes animation state)
  const isLoadingExceptions = isLoadingDate || isLoadingDetail;
  const isAnimatingExceptions =
    !isDateAnimationComplete || !isDetailAnimationComplete;

  // Calculate exception counts from real data
  const dateExceptionCount = dateInconsistencies?.assets.length ?? 0;
  const detailExceptionCount = detailInconsistencies?.assets.length ?? 0;
  const totalExceptions = dateExceptionCount + detailExceptionCount;

  // Only consider exceptions resolved when:
  // 1. Data has loaded
  // 2. Animations have completed
  // 3. There are no exceptions
  const hasExceptions =
    !isLoadingExceptions && !isAnimatingExceptions && totalExceptions > 0;
  const exceptionsResolved =
    !isLoadingExceptions && !isAnimatingExceptions && totalExceptions === 0;

  // Once a latest locked period exists the backend accepts only it, so it is the sole period
  // and may predate the 12-month list.
  const latestLockedPeriod = initialTrueUps?.latestLockedPeriod ?? null;
  const isPeriodPinned = latestLockedPeriod !== null;
  const periods = useMemo(() => {
    // No period until the initial true-ups resolve: a provisional "last month" pin would go
    // out of range the moment a latest locked period arrives.
    if (isLoadingInitialTrueUps) {
      return [];
    }

    return latestLockedPeriod
      ? [latestLockedPeriod]
      : generateInitialTrueUpPeriodList(organizationTimezone);
  }, [isLoadingInitialTrueUps, latestLockedPeriod, organizationTimezone]);

  // The latest locked period when one exists, else last month for a first initial true-up.
  const recommendedPeriod = periods[0] ?? null;

  // Track selected period
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>('');

  // Error state. Curated codes render inline in a plain Alert above
  // the submit button. Unmapped/unexpected errors route to the
  // SupportSnackbar so the "support has been notified" copy is
  // accurate.
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [inlineErrorMessage, setInlineErrorMessage] = useState<string | null>(
    null
  );

  // Create initial true-up mutation
  const createInitialTrueUpMutation = useCreateInitialTrueUp({
    onSuccess: (closeRun) => {
      props.onSuccess?.(closeRun);
    },
    onError: (error) => {
      const fallback = 'An error occurred while creating the initial true-up.';
      if (axios.isAxiosError(error)) {
        const apiError = handleAPIError(error);
        const curated = getUserFacingErrorMessage(apiError);
        if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
          // Curated validation error: render inline. The user is
          // expected to correct the input — support was not notified.
          // Clear any stale fallback snackbar from a previous attempt.
          setInlineErrorMessage(curated);
          setShowErrorSnackbar(false);
          setErrorMessage('');
          return;
        }
        if (!apiError.sentToSentry) {
          captureException(error);
        }
      } else {
        captureException(error);
      }
      // Fallback path: clear any stale inline curated message from a
      // previous attempt so the surfaces don't both render at once.
      setInlineErrorMessage(null);
      setErrorMessage(fallback);
      setShowErrorSnackbar(true);
    },
  });

  // Form setup
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<FormData, unknown, FormOutput>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      year:
        recommendedPeriod?.year ??
        DateTime.now().setZone(organizationTimezone).minus({ months: 1 }).year,
      month:
        recommendedPeriod?.month ??
        DateTime.now().setZone(organizationTimezone).minus({ months: 1 }).month,
      balances: [
        {
          currency: props.currencyCode,
          glInventory: toCurrencyInputValue(null, props.currencyCode),
          glFixedAssets: toCurrencyInputValue(null, props.currencyCode),
          glAccumulatedDepr: toCurrencyInputValue(null, props.currencyCode),
        },
      ],
      onboardingAdjustmentAccount: '',
    },
  });

  // Re-pin whenever the recommendation changes, not just on first load: the initial true-ups
  // arrives after the first render, and the select is disabled, so a stale pin from the
  // pre-resolution guess would be unrecoverable.
  const recommendedPeriodKey = recommendedPeriod
    ? getPeriodKey(recommendedPeriod)
    : null;
  useEffect(() => {
    if (!recommendedPeriod || !recommendedPeriodKey) {
      return;
    }
    setSelectedPeriodKey(recommendedPeriodKey);
    setValue('year', recommendedPeriod.year);
    setValue('month', recommendedPeriod.month);
  }, [recommendedPeriod, recommendedPeriodKey, setValue]);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    const periodKey = event.target.value;
    setSelectedPeriodKey(periodKey);

    // Parse year and month from key and update form
    const [year, month] = periodKey.split('-').map(Number);
    setValue('year', year);
    setValue('month', month);
  };

  const handleFormSubmit: SubmitHandler<FormOutput> = (data) => {
    createInitialTrueUpMutation.mutate({
      year: data.year,
      month: data.month,
      currency: props.currencyCode,
      balances: {
        glInventory: data.balances[0].glInventory,
        glFixedAssets: data.balances[0].glFixedAssets,
        glAccumulatedDepr: data.balances[0].glAccumulatedDepr,
      },
      onboardingAdjustmentAccount: data.onboardingAdjustmentAccount,
    });
  };

  const canSubmit =
    ledgerAccountsComplete &&
    exceptionsResolved &&
    isValid &&
    selectedPeriodKey !== '' &&
    !createInitialTrueUpMutation.isPending;

  const getSubmitTooltip = () => {
    if (!ledgerAccountsComplete) {
      return 'Please configure the required ledger accounts before proceeding';
    }
    if (!exceptionsResolved) {
      return 'Please resolve all asset exceptions before proceeding';
    }
    if (!isValid) {
      return 'Please fill in all required fields correctly';
    }
    return '';
  };

  // Find selected period for display
  const selectedPeriod = periods.find(
    (p) => getPeriodKey(p) === selectedPeriodKey
  );

  // A later currency has to land on the latest locked period; the first initial true-up picks its own period.
  const periodExplanation = isPeriodPinned
    ? 'A new currency must be trued up as of the most recently closed period.'
    : 'You can choose whichever period you want your first initial true-up to use. Your first close will be the month after.';

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3}>
        {/* Ledger accounts configuration check */}
        <Card
          variant="outlined"
          sx={
            ledgerAccountsComplete
              ? {
                  borderColor: 'success.light',
                  borderWidth: 2,
                }
              : {
                  borderColor: 'warning.light',
                  borderWidth: 1,
                }
          }
        >
          <CardHeader
            title="Ledger account configuration"
            subheader="Core ledger accounts (Inventory, Fixed Assets, Accumulated Depreciation) must be configured before creating the initial true-up"
            sx={{ paddingBottom: 0 }}
            avatar={
              ledgerAccountsComplete ? (
                <CheckCircleOutlineRounded color="success" fontSize="small" />
              ) : (
                <WarningAmberOutlined color="warning" fontSize="small" />
              )
            }
            action={
              !ledgerAccountsComplete && (
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<OpenInNewRounded fontSize="small" />}
                  component={Link}
                  href="/organization#ledger-accounts"
                  target="_blank"
                  rel="noopener"
                >
                  Configure ledger accounts
                </Button>
              )
            }
          />
          <CardContent sx={{ padding: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="body1">
                  {configuredCount} of {REQUIRED_ACCOUNT_COUNT} accounts
                  configured
                </Typography>
                <Chip
                  label={ledgerAccountsComplete ? 'Complete' : 'Incomplete'}
                  color={ledgerAccountsComplete ? 'success' : 'warning'}
                  size="small"
                />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={configurationProgress}
                color={ledgerAccountsComplete ? 'success' : 'warning'}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>

            {!ledgerAccountsComplete && (
              <Alert severity="warning">
                Please configure the required ledger accounts (Inventory, Fixed
                Assets, and Accumulated Depreciation) in{' '}
                <Link
                  href="/organization#ledger-accounts"
                  target="_blank"
                  rel="noopener"
                >
                  organization settings
                </Link>{' '}
                before creating the initial true-up.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Asset exceptions check */}
        <Card
          variant="outlined"
          sx={
            isLoadingExceptions || isAnimatingExceptions
              ? {
                  borderColor: 'info.light',
                  borderWidth: 1,
                }
              : exceptionsResolved
                ? {
                    borderColor: 'success.light',
                    borderWidth: 2,
                  }
                : {
                    borderColor: 'error.light',
                    borderWidth: 1,
                  }
          }
        >
          <CardHeader
            title="Asset exceptions"
            subheader="Review and resolve any asset exceptions before completing the initial true-up"
            sx={{ paddingBottom: 0 }}
            avatar={
              isLoadingExceptions || isAnimatingExceptions ? (
                <Autorenew
                  color="info"
                  fontSize="small"
                  sx={{
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              ) : exceptionsResolved ? (
                <CheckCircleOutlineRounded color="success" fontSize="small" />
              ) : (
                <ErrorOutlineRounded color="error" fontSize="small" />
              )
            }
          />
          <CardContent sx={{ paddingX: 3 }}>
            {(isLoadingExceptions || isAnimatingExceptions) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Loading asset exception data...
              </Alert>
            )}

            {hasExceptions && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {pluralize(totalExceptions, { singular: 'asset' })} require
                attention. Please resolve all exceptions before proceeding.
              </Alert>
            )}

            {exceptionsResolved && (
              <Alert severity="success" sx={{ mb: 2 }}>
                No asset exceptions require attention.
              </Alert>
            )}

            {/* Date exceptions summary */}
            <Box sx={{ mb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Stack direction="column">
                  <Typography variant="subtitle1">Date exceptions</Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Assets with date inconsistencies such as in-service dates
                    before inventory dates, movements before inventory, etc.
                  </Typography>
                </Stack>
                {dateExceptionCount > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewRounded fontSize="small" />}
                    component={Link}
                    href="/reports/exception/asset/date"
                    target="_blank"
                    rel="noopener"
                  >
                    Review date exceptions
                  </Button>
                )}
              </Stack>
              <ExceptionSummaryCard
                stats={{
                  totalAnalyzed: dateInconsistencies?.totalAnalyzed ?? 0,
                  withExceptions: dateExceptionCount,
                }}
                isLoading={isLoadingDate}
                onAnimationComplete={() => setIsDateAnimationComplete(true)}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Detail exceptions summary */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Stack direction="column">
                  <Typography variant="subtitle1">Detail exceptions</Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Assets with detail inconsistencies such as missing cost
                    basis, salvage value issues, missing useful life, etc.
                  </Typography>
                </Stack>
                {detailExceptionCount > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewRounded fontSize="small" />}
                    component={Link}
                    href="/reports/exception/asset/detail"
                    target="_blank"
                    rel="noopener"
                  >
                    Review detail exceptions
                  </Button>
                )}
              </Stack>

              <ExceptionSummaryCard
                stats={{
                  totalAnalyzed: detailInconsistencies?.totalAnalyzed ?? 0,
                  withExceptions: detailExceptionCount,
                }}
                isLoading={isLoadingDetail}
                onAnimationComplete={() => setIsDetailAnimationComplete(true)}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Form fields */}
        <Card variant="outlined">
          <CardHeader
            title="Current general ledger (GL) balances"
            subheader="Enter your general ledger account balances as of the selected period end date"
            sx={{ paddingBottom: 1 }}
          />
          <CardContent sx={{ padding: 3 }}>
            <Stack spacing={3}>
              {/* Period selector, fixed once a latest locked period exists; the info tooltip explains why */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="initial-true-up-period-label">
                      Period
                    </InputLabel>
                    <Tooltip arrow placement="top" title={periodExplanation}>
                      <span>
                        <Select
                          fullWidth
                          labelId="initial-true-up-period-label"
                          id="initial-true-up-period"
                          value={selectedPeriodKey}
                          label="Period"
                          onChange={handlePeriodChange}
                          disabled={isPeriodPinned || !ledgerAccountsComplete}
                        >
                          {periods.length === 0 ? (
                            <MenuItem value="">No periods available</MenuItem>
                          ) : (
                            periods.map((period) => {
                              const isRecommended =
                                recommendedPeriod &&
                                period.year === recommendedPeriod.year &&
                                period.month === recommendedPeriod.month;
                              return (
                                <MenuItem
                                  key={getPeriodKey(period)}
                                  value={getPeriodKey(period)}
                                  sx={{
                                    fontWeight: isRecommended
                                      ? 'bold'
                                      : 'normal',
                                    color: isRecommended
                                      ? 'primary.main'
                                      : 'inherit',
                                  }}
                                >
                                  {formatPeriod(period)}
                                  {isRecommended &&
                                    (isPeriodPinned
                                      ? ' (required)'
                                      : ' (recommended)')}
                                </MenuItem>
                              );
                            })
                          )}
                        </Select>
                      </span>
                    </Tooltip>
                  </FormControl>
                  <Tooltip arrow placement="top" title={periodExplanation}>
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </Stack>
                {selectedPeriod && (
                  <FormHelperText variant="outlined">
                    Enter GL balances as of{' '}
                    {DateTime.fromObject({
                      year: selectedPeriod.year,
                      month: selectedPeriod.month,
                    })
                      .endOf('month')
                      .toLocaleString(DateTime.DATE_FULL)}
                  </FormHelperText>
                )}
              </Box>

              {/* GL balances for the single currency this run onboards */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle2">
                    {props.currencyCode} opening balances
                  </Typography>
                  <FormHelperText>
                    Enter the general ledger balances below in{' '}
                    {props.currencyCode}.
                  </FormHelperText>
                </Box>

                {/* Inventory balance */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormCurrencyTextField
                    fullWidth
                    label="Inventory balance"
                    name="balances.0.glInventory"
                    control={control}
                    lockTo={props.currencyCode}
                    compact
                    disabled={!ledgerAccountsComplete}
                  />
                  <Tooltip
                    arrow
                    placement="top"
                    title="Current inventory balance from your general ledger"
                  >
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </Stack>

                {/* Fixed asset cost */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormCurrencyTextField
                    fullWidth
                    label="Fixed asset cost"
                    name="balances.0.glFixedAssets"
                    control={control}
                    lockTo={props.currencyCode}
                    compact
                    disabled={!ledgerAccountsComplete}
                  />
                  <Tooltip
                    arrow
                    placement="top"
                    title="Total fixed asset cost basis from your general ledger"
                  >
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </Stack>

                {/* Accumulated depreciation */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormCurrencyTextField
                    fullWidth
                    label="Accumulated depreciation"
                    name="balances.0.glAccumulatedDepr"
                    control={control}
                    lockTo={props.currencyCode}
                    compact
                    disabled={!ledgerAccountsComplete}
                  />
                  <Tooltip
                    arrow
                    placement="top"
                    title="Total accumulated depreciation from your general ledger (must not exceed fixed asset cost)"
                  >
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </Stack>
              </Box>

              {/* Onboarding adjustment account name */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Controller
                  name="onboardingAdjustmentAccount"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Onboarding adjustment account"
                      placeholder="e.g., 99030 Asset Subledger - Onboarding Adjustment"
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      disabled={!ledgerAccountsComplete}
                    />
                  )}
                />
                <Tooltip
                  arrow
                  placement="top"
                  title="The account name for the onboarding adjustment journal entry line item (e.g., '99030 Asset Subledger - Onboarding Adjustment')"
                >
                  <InfoOutlined fontSize="small" color="action" />
                </Tooltip>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Information about what happens next */}
        <Box sx={{ width: '50%', alignSelf: 'flex-end' }}>
          <Alert severity="info">
            <AlertTitle>
              You will be able to review the journal entry before locking
            </AlertTitle>
            Creating the initial true-up will compare your current GL balances
            with Hardfin's computed balances and generate a journal entry to
            align your general ledger with Hardfin.
          </Alert>
        </Box>

        {inlineErrorMessage && (
          <Alert severity="error" onClose={() => setInlineErrorMessage(null)}>
            {inlineErrorMessage}
          </Alert>
        )}

        {/* Submit button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip arrow title={!canSubmit ? getSubmitTooltip() : ''}>
            <Box>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="small"
                disabled={!canSubmit}
              >
                {createInitialTrueUpMutation.isPending
                  ? 'Calculating initial true-up...'
                  : 'Calculate initial true-up'}
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </Stack>

      <SupportSnackbar
        isOpen={showErrorSnackbar}
        onClose={() => setShowErrorSnackbar(false)}
        message={errorMessage}
      />
    </Box>
  );
}
